import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Receipt, Plus, DownloadSimple } from "@phosphor-icons/react";

const STATUS_STYLE = {
  paid: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
  partial: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10",
  pending: "border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10",
  sponsored: "border-[var(--vs-secondary)]/40 text-[var(--vs-secondary)] bg-[var(--vs-secondary)]/10",
};

const downloadReceipt = (feeId) => window.open(`${API}/fees/${feeId}/receipt`, "_blank", "noopener");

export default function Fees() {
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", fee_head: "hostel", period: "", amount: 2500 });
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");

  const refresh = () => api.get("/hostel/fees").then((r) => setFees(r.data));
  useEffect(() => {
    refresh();
    api.get("/students").then((r) => setStudents(r.data));
    setForm((f) => ({ ...f, period: new Date().toISOString().slice(0, 7) }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hostel/fees", { ...form, amount: Number(form.amount) });
      toast.success("Fee record created");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const pay = async () => {
    try {
      const res = await api.post(`/hostel/fees/${payTarget.id}/pay`, { amount: Number(payAmount) });
      toast.success(`Receipt ${res.data.receipt_no} • ${res.data.status.toUpperCase()}`);
      setPayOpen(false);
      setPayAmount("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="Fee Collection" subtitle="Receipts auto-numbered. Donor-sponsored students auto-excluded from billing.">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[var(--vs-text-secondary)]">
          {fees.length} records • {fees.filter((f) => f.status === "pending").length} pending • {fees.filter((f) => f.status === "sponsored").length} sponsored
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-fee-btn" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
              <Plus size={14} className="mr-1.5" /> New fee
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create fee record</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Student</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose student" /></SelectTrigger>
                  <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.vs_id} — {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fee head</Label>
                <Select value={form.fee_head} onValueChange={(v) => setForm({ ...form, fee_head: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hostel">Hostel</SelectItem>
                    <SelectItem value="tuition">Tuition</SelectItem>
                    <SelectItem value="exam">Exam</SelectItem>
                    <SelectItem value="activity">Activity</SelectItem>
                    <SelectItem value="library">Library</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Period (YYYY-MM)</Label><Input required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
                <div><Label>Amount (₹)</Label><Input required type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              </div>
              <Button type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">VS-ID</th><th className="px-4 py-3">Head</th>
            <th className="px-4 py-3">Period</th><th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Paid</th><th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Receipt</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {fees.map((f) => (
              <tr key={f.id} data-testid={`fee-row-${f.id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{f.student_vs_id}</td>
                <td className="px-4 py-3 capitalize">{f.fee_head}</td>
                <td className="px-4 py-3 font-mono text-xs">{f.period}</td>
                <td className="px-4 py-3 font-mono">₹{f.amount}</td>
                <td className="px-4 py-3 font-mono">₹{f.paid}</td>
                <td className="px-4 py-3"><Badge variant="outline" className={STATUS_STYLE[f.status]}>{f.status}</Badge></td>
                <td className="px-4 py-3 font-mono text-xs">{f.receipt_no || "—"}</td>
                <td className="px-4 py-3">
                  {(f.status === "pending" || f.status === "partial") && (
                    <Button size="sm" variant="outline" data-testid={`pay-fee-${f.id}`} onClick={() => { setPayTarget(f); setPayAmount(String(f.amount - f.paid)); setPayOpen(true); }}>
                      <Receipt size={12} className="mr-1.5" />Pay
                    </Button>
                  )}
                  {(f.status === "paid" || f.status === "sponsored") && (
                    <Button size="sm" variant="outline" data-testid={`download-receipt-${f.id}`} onClick={() => downloadReceipt(f.id)}>
                      <DownloadSimple size={12} className="mr-1.5" />Receipt
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {fees.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No fee records.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Collect payment</DialogTitle></DialogHeader>
          {payTarget && (
            <div className="space-y-3">
              <div className="text-sm">{payTarget.student_vs_id} • {payTarget.fee_head} • {payTarget.period}</div>
              <div>
                <Label>Amount (₹)</Label>
                <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
              </div>
              <Button onClick={pay} className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Confirm payment</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
